import { type ReactNode, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuthStore } from "../stores/auth";
import { Clock, ShieldOff } from "lucide-react";
import { StatusDialog } from "./StatusDialog";
import type { User } from "../types/api";

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: User["role"][];
  requireActive?: boolean;
}

export function ProtectedRoute({
  children,
  allowedRoles,
  requireActive = true,
}: ProtectedRouteProps) {
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const isLoading = useAuthStore(s => s.isLoading);
  const logout = useAuthStore(s => s.logout);
  const navigate = useNavigate();
  const hadAccessRef = useRef(false);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireActive && user?.registration_status !== "active") {
    return (
      <StatusDialog
        icon={Clock}
        iconClassName="text-muted-foreground"
        mediaClassName="bg-muted"
        title="Account Pending Approval"
        description="Your account is awaiting administrator approval."
        actionLabel="Go to Login"
        onAction={() => {
          logout();
          navigate("/login");
        }}
      />
    );
  }

  const roleDenied = allowedRoles && !allowedRoles.includes(user!.role);

  if (roleDenied && hadAccessRef.current) {
    return (
      <>
        {children}
        <StatusDialog
          icon={ShieldOff}
          iconClassName="text-warning"
          mediaClassName="bg-warning/10"
          title="Permissions Changed"
          description="Your role has been updated. You no longer have access to this page."
          actionLabel="Go to Dashboard"
          onAction={() => navigate("/")}
        />
      </>
    );
  }

  if (roleDenied) {
    return (
      <StatusDialog
        icon={ShieldOff}
        iconClassName="text-destructive"
        mediaClassName="bg-destructive/10"
        title="Access Denied"
        description="You don't have permission to access this page."
        actionLabel="Go to Dashboard"
        onAction={() => navigate("/")}
      />
    );
  }

  hadAccessRef.current = true;
  return <>{children}</>;
}
